/**
 * Maps caregiver onboarding + local billing snapshot to the product journey
 * (registration → documents → fee → Ops review → interview → approval → ACTIVE).
 */

export function getCaregiverJourneyPhases(onboarding, billingSnapshot = {}) {
  const profileDone = Boolean(onboarding?.profile?.completed);
  const docsDone = Boolean(
    (onboarding?.kyc?.frontUploaded && onboarding?.kyc?.backUploaded) ||
      onboarding?.status?.profileComplete ||
      onboarding?.status?.profile_completed
  );
  const feePaid = isCaregiverSubscriptionPaid(billingSnapshot);
  const adminApproved = Boolean(onboarding?.status?.adminApproved);
  const marketplaceVisible = Boolean(onboarding?.status?.marketplaceVisibility);
  const interviewLogged =
    Array.isArray(onboarding?.intakeAssessments) && onboarding.intakeAssessments.length > 0;

  const phases = [
    {
      id: "reg",
      title: "Caregiver registration",
      description: "Account created and profile submitted.",
      done: profileDone,
    },
    {
      id: "docs",
      title: "Upload documents",
      description: "Aadhaar front & back (and police certificate) from Complete Profile → Documents.",
      done: docsDone,
    },
    {
      id: "fee",
      title: "Pay verification / subscription fee",
      description: "NEMA access, verification, and subscription charges (see Billing & insurance).",
      done: feePaid,
    },
    {
      id: "review",
      title: "Admin review starts",
      description: "Ops opens your file after payment is confirmed and validates submissions.",
      done: adminApproved,
    },
    {
      id: "interview",
      title: "Interview & assessments",
      description: interviewLogged
        ? "Ops has saved your intake interview summary (view it under Approval status)."
        : "After your live interview, Ops logs duration and behaviour score—you will see it here when saved.",
      done: interviewLogged,
    },
    {
      id: "approval",
      title: "Admin approval",
      description: "Final approval to publish you to the marketplace.",
      done: adminApproved,
    },
    {
      id: "active",
      title: "Caregiver ACTIVE",
      description: "Listed for bookings; you can accept requests.",
      done: marketplaceVisible,
    },
  ];

  let currentIndex = phases.findIndex((p) => !p.done);
  if (currentIndex < 0) {
    currentIndex = phases.length - 1;
  }

  return { phases, currentIndex };
}

export function isCaregiverProfileComplete(onboarding) {
  if (!onboarding || typeof onboarding !== "object") return false;
  return Boolean(
    onboarding?.profile?.completed ||
      onboarding?.status?.profileComplete ||
      onboarding?.status?.profile_completed
  );
}

/**
 * New signup and incomplete caregiver accounts should continue in profile setup.
 * Returning caregivers with a completed profile can go straight to the dashboard.
 */
export function resolveCaregiverPostAuthRoute(onboarding, { isNewRegistration = false } = {}) {
  const profileComplete = isCaregiverProfileComplete(onboarding);
  if (isNewRegistration) {
    return {
      path: "/caregiver/register",
      showProfileHint: false,
      isNewRegistration: true,
    };
  }
  if (!profileComplete) {
    return {
      path: "/caregiver/complete-profile",
      showProfileHint: false,
      isNewRegistration: false,
    };
  }
  return {
    path: "/caregiver",
    showProfileHint: false,
    isNewRegistration: false,
  };
}

export function getCaregiverProfileSetupHint(onboarding) {
  if (isCaregiverProfileComplete(onboarding)) return "";
  const step = Number(onboarding?.onboardingStep || 1);
  if (step <= 1) {
    return "Complete your caregiver profile (skills, experience, and photo) to continue onboarding.";
  }
  return "Finish your profile setup so Ops can review your application.";
}

export function isTherapistSubscriptionActive(therapistSubscription = null) {
  if (!therapistSubscription || typeof therapistSubscription !== "object") return false;
  const sub = therapistSubscription.subscription || therapistSubscription;
  if (!sub || typeof sub !== "object") return false;
  const status = String(sub.status || "").toUpperCase();
  if (status === "ACTIVE" || status === "TRIALING") return true;
  const endRaw = sub.currentPeriodEnd || sub.current_period_end;
  if (endRaw) {
    const end = new Date(endRaw).getTime();
    if (Number.isFinite(end) && end > Date.now()) return true;
  }
  return false;
}

export function isCaregiverSubscriptionPaid(billingSnapshot = {}, therapistSubscription = null) {
  if (Boolean(billingSnapshot?.reactivationPaid ?? billingSnapshot?.lastPaidAt)) return true;
  return isTherapistSubscriptionActive(therapistSubscription);
}

export function isCaregiverDocsComplete(onboarding) {
  return (
    Boolean(
      onboarding?.kyc?.frontUploaded &&
        onboarding?.kyc?.backUploaded &&
        onboarding?.kyc?.selfieUploaded
    ) && Boolean(onboarding?.police?.documentUploaded)
  );
}

/** Profile saved; verification / subscription fee not yet paid. */
export function shouldHintCaregiverSubscription() {
  // Caregiver platform subscription is paused — never gate onboarding or duty on it.
  return false;
}

/** Show Pay subscription CTA once profile is complete and fee is unpaid. */
export function shouldShowProceedToPaymentInOnboarding() {
  return false;
}

export function getCaregiverSubscriptionHint() {
  return "Caregiver subscription is currently paused. You can continue onboarding and accept bookings without purchasing a platform subscription.";
}

/** @deprecated alias — use shouldHintCaregiverSubscription */
export function canProceedToCaregiverPayment(onboarding, billingSnapshot = {}, therapistSubscription = null) {
  return shouldHintCaregiverSubscription(onboarding, billingSnapshot, therapistSubscription);
}
